import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { adminApi, formatApiError } from "./api";
import { SendMessageDialog } from "./UserDialogs";
import { toast } from "sonner";
import { CheckCircle2, Send, Car, CreditCard, AlertTriangle, Pause, Loader2, ShieldAlert, Phone, Mail, Zap, MapPin, Calendar, Users, Route, MoreVertical, Users2, MessageSquare, UserCog, ChevronRight, ChevronDown, TrendingUp, ArrowRight, Receipt, Clock, Wallet } from "lucide-react";

const money = (n) => `$${(n ?? 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function PayoutDrawer({ payout, onClose, onDone }) {
  const [loading, setLoading] = useState("");
  const [confirm, setConfirm] = useState(false);
  if (!payout) return null;

  const LARGE_PAYOUT = 100; // CAD — flag extra warning above this
  const isLarge = (payout.amount || 0) >= LARGE_PAYOUT;

  const close = () => { setConfirm(false); onClose(); };

  const act = async (action, label) => {
    setLoading(action);
    try {
      await adminApi.post(`/finance/payout/${payout.id}`, { action });
      toast.success(`${label} — ${payout.driver} (${money(payout.amount)})`);
      setConfirm(false);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading("");
    }
  };

  const row = (label, value) => (
    <div className="flex justify-between border-b border-[#12142b]/6 py-3 text-sm last:border-0">
      <span className="text-[#565b73]">{label}</span>
      <span className="font-semibold text-[#12142b]">{value}</span>
    </div>
  );

  return (
    <Dialog open={!!payout} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="payout-drawer">
        <DialogHeader><DialogTitle>Driver Payout</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-[#2340b5]/10 font-bold text-[#2340b5]">
            {payout.driver.split(" ").map((w) => w[0]).join("").slice(0, 2)}
          </span>
          <div>
            <p className="font-display text-lg font-bold text-[#12142b]">{payout.driver}</p>
            <p className="text-sm text-[#565b73]">Eligible for payout</p>
          </div>
        </div>
        <div className="mt-4 rounded-xl bg-[#f4f6fb] p-4 text-center">
          <p className="text-xs text-[#565b73]">Payout amount</p>
          <p className="font-display text-3xl font-black text-[#12142b]">{money(payout.amount)}</p>
        </div>
        <div className="mt-2">
          {payout.trips != null && row(<span className="flex items-center gap-2"><Car className="h-4 w-4 text-[#565b73]" /> Completed trips</span>, payout.trips)}
          {row(<span className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-[#565b73]" /> Method</span>, `${payout.method}${payout.last4 ? ` ••${payout.last4}` : ""}`)}
          {payout.status && row(<span className="flex items-center gap-2"><Wallet className="h-4 w-4 text-[#565b73]" /> Status</span>, payout.status === "hold" ? "Processing" : "Pending")}
        </div>
        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <button
            data-testid="payout-processing"
            disabled={!!loading || confirm}
            onClick={() => act("hold", "Marked as processing")}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#7c3aed]/10 px-4 py-3 text-sm font-bold text-[#7c3aed] hover:bg-[#7c3aed]/20 disabled:opacity-60"
          >
            <Clock className="h-4 w-4" /> {loading === "hold" ? "Updating…" : "Mark Processing"}
          </button>
          <button
            data-testid="payout-approve"
            disabled={!!loading || confirm}
            onClick={() => setConfirm(true)}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#16a34a] px-4 py-3 text-sm font-bold text-white hover:bg-[#15803d] disabled:opacity-60"
          >
            <CheckCircle2 className="h-4 w-4" /> Approve Payout
          </button>
        </DialogFooter>

        {confirm && (
          <div data-testid="payout-approve-confirm" className={`mt-3 rounded-xl border p-4 ${isLarge ? "border-[#e4002b]/30 bg-[#e4002b]/5" : "border-[#16a34a]/25 bg-[#16a34a]/5"}`}>
            <p className="flex items-center gap-2 text-sm font-bold text-[#12142b]">
              <ShieldAlert className={`h-4 w-4 ${isLarge ? "text-[#e4002b]" : "text-[#16a34a]"}`} />
              Confirm payout of {money(payout.amount)}
            </p>
            <p className="mt-1 text-xs text-[#565b73]">
              This settles <span className="font-semibold text-[#12142b]">{money(payout.amount)}</span> to <span className="font-semibold text-[#12142b]">{payout.driver}</span> and marks it paid. This can’t be undone.
              {isLarge && <span className="mt-1 block font-semibold text-[#e4002b]">Large payout — please double-check the amount and driver.</span>}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                data-testid="payout-approve-cancel"
                disabled={!!loading}
                onClick={() => setConfirm(false)}
                className="flex-1 rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73] hover:bg-[#f4f6fb] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                data-testid="payout-approve-confirm-yes"
                disabled={!!loading}
                onClick={() => act("approve", "Payout approved")}
                className="flex flex-[2] items-center justify-center gap-2 rounded-xl bg-[#16a34a] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#15803d] disabled:opacity-60"
              >
                <CheckCircle2 className="h-4 w-4" /> {loading === "approve" ? "Approving…" : `Yes, approve ${money(payout.amount)}`}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RefundDialog({ refund, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState("");
  if (!refund) return null;

  const act = async (action) => {
    if (action === "decline" && !reason.trim()) {
      toast.error("Please add a reason before declining");
      return;
    }
    setLoading(action);
    try {
      await adminApi.post(`/finance/refund/${refund.id}/process`, { action, reason });
      toast.success(action === "approve" ? "Refund approved" : "Refund declined");
      setReason("");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading("");
    }
  };

  return (
    <Dialog open={!!refund} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="refund-dialog">
        <DialogHeader><DialogTitle>Review Refund</DialogTitle></DialogHeader>
        <div className="rounded-xl bg-[#f4f6fb] p-4">
          <div className="flex justify-between text-sm"><span className="text-[#565b73]">Rider</span><span className="font-semibold text-[#12142b]">{refund.rider}</span></div>
          <div className="mt-1 flex justify-between text-sm"><span className="text-[#565b73]">Driver</span><span className="font-semibold text-[#12142b]">{refund.driver}</span></div>
          <div className="mt-1 flex justify-between text-sm"><span className="text-[#565b73]">Booking amount (excl. service fee)</span><span className="font-semibold text-[#12142b]">{money(refund.booking_base ?? refund.booking_total ?? 0)}</span></div>
          <div className="mt-1 flex justify-between text-sm"><span className="text-[#565b73]">Rider refund ({refund.rider_pct ?? "—"}%)</span><span className="font-semibold text-[#12142b]">{money(refund.refund)}</span></div>
          <div className="mt-1 flex justify-between text-sm"><span className="text-[#565b73]">Driver compensation ({refund.driver_pct ?? "—"}%)</span><span className="font-semibold text-[#12142b]">{money(refund.driver_share ?? refund.fee)}</span></div>
        </div>
        <p className="mt-2 text-xs text-[#565b73]">Approving releases both shares at once — the rider's refund and the driver's compensation go to their wallets.</p>
        <label className="mt-4 block text-sm font-semibold text-[#12142b]">Reason / note</label>
        <textarea
          data-testid="refund-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Add a reason for approving or declining this refund…"
          className="mt-1 w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
        />
        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <button
            data-testid="refund-decline"
            disabled={!!loading}
            onClick={() => act("decline")}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#e4002b]/10 px-4 py-3 text-sm font-bold text-[#e4002b] hover:bg-[#e4002b]/20 disabled:opacity-60"
          >
            <AlertTriangle className="h-4 w-4" /> {loading === "decline" ? "Declining…" : "Decline"}
          </button>
          <button
            data-testid="refund-approve"
            disabled={!!loading}
            onClick={() => act("approve")}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#16a34a] px-4 py-3 text-sm font-bold text-white hover:bg-[#15803d] disabled:opacity-60"
          >
            <CheckCircle2 className="h-4 w-4" /> {loading === "approve" ? "Approving…" : "Approve Refund"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const STATUS_STYLE = {
  processed: { label: "Processed", color: "#16a34a", bg: "#16a34a14" },
  pending: { label: "Pending", color: "#b45309", bg: "#f59e0b14" },
  hold: { label: "On Hold", color: "#7c3aed", bg: "#7c3aed14" },
};

export function PayoutsAllDialog({ open, onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const [loadingId, setLoadingId] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [tripsFor, setTripsFor] = useState(null);
  const LARGE_PAYOUT = 100; // CAD — require confirm above this even without disputes

  const load = useCallback(() => {
    adminApi.get("/finance/payouts").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { if (open) { setItems(null); setExpanded(null); load(); } }, [open, load]);

  const act = async (p, action, label) => {
    setLoadingId(`${p.id}-${action}`);
    try {
      await adminApi.post(`/finance/payout/${p.id}`, { action });
      toast.success(`${label} — ${p.driver} (${money(p.amount)})`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingId("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="payouts-all-dialog">
        <DialogHeader>
          <DialogTitle>Completed Trips — All Payouts</DialogTitle>
          <p className="text-sm text-[#565b73]">Review each driver&apos;s payout, status and any open disputes before processing.</p>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {!items && <p className="py-10 text-center text-sm text-[#565b73]">Loading payouts…</p>}
          {items && items.length === 0 && <p className="py-10 text-center text-sm text-[#565b73]">No payouts found.</p>}
          {items && items.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]">
                  <th className="py-2 pr-2">Driver</th>
                  <th className="py-2 pr-2">Amount</th>
                  <th className="py-2 pr-2">Method</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const st = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
                  const busy = loadingId.startsWith(p.id);
                  return (
                    <Fragment key={p.id}>
                    <tr data-testid={`payout-all-row-${p.id}`} className="border-b border-[#12142b]/5">
                      <td className="py-3 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#2340b5]/10 text-xs font-bold text-[#2340b5]">
                            {(p.driver || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                          </span>
                          <span className="font-medium text-[#12142b]">{p.driver}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-2 font-semibold text-[#12142b]">{money(p.amount)}</td>
                      <td className="py-3 pr-2 text-[#565b73]">{p.method}{p.last4 ? ` ••${p.last4}` : ""}</td>
                      <td className="py-3 pr-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: st.color, backgroundColor: st.bg }}>
                            {st.label}
                          </span>
                          {p.has_dispute && (
                            <button
                              data-testid={`payout-dispute-${p.id}`}
                              onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                              className="flex items-center gap-1 rounded-full bg-[#e4002b]/12 px-2 py-1 text-xs font-semibold text-[#e4002b] transition-colors hover:bg-[#e4002b]/20"
                            >
                              <ShieldAlert className="h-3 w-3" /> Open Dispute{p.disputes?.length ? ` (${p.disputes.length})` : ""}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-2">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`payout-viewtrip-${p.id}`}
                            onClick={() => setTripsFor(p)}
                            className="flex items-center gap-1.5 rounded-lg border border-[#2340b5]/25 px-3 py-1.5 text-xs font-semibold text-[#2340b5] transition-colors hover:bg-[#2340b5]/8"
                          >
                            <Route className="h-3.5 w-3.5" /> View Trip
                          </button>
                          <button
                            data-testid={`payout-process-${p.id}`}
                            disabled={busy || p.status === "processed"}
                            onClick={() => (p.has_dispute || p.amount >= LARGE_PAYOUT ? setConfirm(p) : act(p, "approve", "Payment processed"))}
                            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-40 ${p.has_dispute ? "bg-[#d97706] hover:bg-[#b45309]" : "bg-[#16a34a] hover:bg-[#15803d]"}`}
                          >
                            {loadingId === `${p.id}-approve` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : p.has_dispute ? <ShieldAlert className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Process Payment
                          </button>
                          <button
                            data-testid={`payout-hold-${p.id}`}
                            disabled={busy || p.status === "hold"}
                            onClick={() => act(p, "hold", "Payout held")}
                            className="flex items-center gap-1.5 rounded-lg border border-[#7c3aed]/30 px-3 py-1.5 text-xs font-semibold text-[#7c3aed] transition-colors hover:bg-[#7c3aed]/8 disabled:opacity-40"
                          >
                            {loadingId === `${p.id}-hold` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3.5 w-3.5" />} Hold
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded === p.id && (
                      <tr data-testid={`payout-disputes-${p.id}`}>
                        <td colSpan={5} className="bg-[#e4002b]/4 px-2 py-3">
                          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#e4002b]">
                            <ShieldAlert className="h-3.5 w-3.5" /> Open disputes against {p.driver}
                          </p>
                          <div className="space-y-2">
                            {(p.disputes || []).map((d) => (
                              <div key={d.id} className="rounded-lg border border-[#e4002b]/15 bg-white p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-sm font-semibold text-[#12142b]">{d.route}</p>
                                  <span className="shrink-0 text-xs font-semibold text-[#e4002b]">{money(d.amount)} refund</span>
                                </div>
                                <p className="mt-0.5 text-xs text-[#565b73]">
                                  Rider: {d.rider}{d.cancelled_at ? ` · ${new Date(d.cancelled_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" })}` : ""}
                                </p>
                                <p className="mt-1 text-xs text-[#12142b]"><span className="font-semibold">Reason:</span> {d.reason}</p>
                              </div>
                            ))}
                            {(!p.disputes || p.disputes.length === 0) && (
                              <p className="text-xs text-[#565b73]">No detailed dispute records available.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="payout-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#e4002b]">
              <ShieldAlert className="h-5 w-5" /> {confirm?.has_dispute ? "Open dispute — hold on" : "Confirm large payout"}
            </DialogTitle>
            <p className="text-sm text-[#565b73]">
              {confirm?.has_dispute ? (
                <>
                  {confirm?.driver} has {confirm?.disputes?.length || 0} open dispute{(confirm?.disputes?.length || 0) === 1 ? "" : "s"}.
                  Releasing {confirm ? money(confirm.amount) : ""} now may be difficult to recover if the dispute is upheld.
                </>
              ) : (
                <>
                  You’re about to settle <span className="font-semibold text-[#12142b]">{confirm ? money(confirm.amount) : ""}</span> to <span className="font-semibold text-[#12142b]">{confirm?.driver}</span>. This marks the payout paid and can’t be undone.
                </>
              )}
            </p>
          </DialogHeader>
          {confirm?.disputes?.length > 0 && (
            <div className="max-h-[30vh] space-y-2 overflow-y-auto">
              {confirm.disputes.map((d) => (
                <div key={d.id} className="rounded-lg border border-[#e4002b]/15 bg-[#e4002b]/4 p-3">
                  <p className="text-sm font-semibold text-[#12142b]">{d.route}</p>
                  <p className="mt-0.5 text-xs text-[#565b73]">Rider: {d.rider} · {money(d.amount)} refund</p>
                  <p className="mt-1 text-xs text-[#12142b]"><span className="font-semibold">Reason:</span> {d.reason}</p>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button
              data-testid="payout-confirm-cancel"
              onClick={() => setConfirm(null)}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73]"
            >
              Cancel
            </button>
            <button
              data-testid="payout-confirm-process"
              onClick={() => { const c = confirm; setConfirm(null); act(c, "approve", "Payment processed"); }}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors ${confirm?.has_dispute ? "bg-[#d97706] hover:bg-[#b45309]" : "bg-[#16a34a] hover:bg-[#15803d]"}`}
            >
              <CheckCircle2 className="h-4 w-4" /> {confirm?.has_dispute ? "Process Anyway" : `Approve ${confirm ? money(confirm.amount) : ""}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DriverTripsDialog request={tripsFor} onClose={() => setTripsFor(null)} />
    </Dialog>
  );
}

export function EtransferAllDialog({ open, onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const [loadingId, setLoadingId] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [tripsFor, setTripsFor] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/finance/etransfer-queue").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { if (open) { setItems(null); setConfirm(null); load(); } }, [open, load]);

  const pay = async (e) => {
    setLoadingId(e.id);
    try {
      await adminApi.post(`/finance/payout/${e.id}`, { action: "approve" });
      toast.success(`E-Transfer paid — ${e.requester} (${money(e.amount)})`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingId("");
    }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-CA", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="etransfer-all-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-[#7c3aed]" /> Instant E-Transfer Queue</DialogTitle>
          <p className="text-sm text-[#565b73]">Everyone requesting an instant payout, with their contact details and status.</p>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {!items && <p className="py-10 text-center text-sm text-[#565b73]">Loading requests…</p>}
          {items && items.length === 0 && <p className="py-10 text-center text-sm text-[#565b73]">No instant e-transfer requests.</p>}
          {items && items.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]">
                  <th className="py-2 pr-2">Requester</th>
                  <th className="py-2 pr-2">Contact</th>
                  <th className="py-2 pr-2">Amount</th>
                  <th className="py-2 pr-2">Requested</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const paid = e.status === "paid";
                  return (
                    <tr key={e.id} data-testid={`etransfer-row-${e.id}`} className="border-b border-[#12142b]/5 last:border-0">
                      <td className="py-3 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#7c3aed]/10 text-xs font-bold text-[#7c3aed]">
                            {(e.requester || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                          </span>
                          <div>
                            <p className="font-medium text-[#12142b]">{e.requester}</p>
                            <p className="text-xs capitalize text-[#565b73]">{e.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-2">
                        <p className="flex items-center gap-1.5 text-xs text-[#12142b]"><Phone className="h-3 w-3 text-[#565b73]" /> {e.phone}</p>
                        <p className="flex items-center gap-1.5 text-xs text-[#565b73]"><Mail className="h-3 w-3" /> {e.email}</p>
                      </td>
                      <td className="py-3 pr-2 font-semibold text-[#12142b]">{money(e.amount)}<span className="block text-xs font-normal text-[#565b73]">{e.method}{e.last4 ? ` ••${e.last4}` : ""}</span></td>
                      <td className="py-3 pr-2 text-[#565b73]">{fmtDate(e.requested_at)}</td>
                      <td className="py-3 pr-2">
                        <div className="flex flex-col items-start gap-1">
                          <span className="rounded-full px-2.5 py-1 text-xs font-semibold"
                            style={{ color: paid ? "#16a34a" : "#b45309", backgroundColor: paid ? "#16a34a14" : "#f59e0b14" }}>
                            {paid ? "Paid" : "Pending"}
                          </span>
                          {e.has_dispute && (
                            <span className="flex items-center gap-1 rounded-full bg-[#e4002b]/12 px-2 py-1 text-xs font-semibold text-[#e4002b]">
                              <ShieldAlert className="h-3 w-3" /> Open Dispute{e.disputes?.length ? ` (${e.disputes.length})` : ""}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-2 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`etransfer-viewtrip-${e.id}`}
                            onClick={() => setTripsFor(e)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#2340b5]/25 px-3 py-1.5 text-xs font-semibold text-[#2340b5] transition-colors hover:bg-[#2340b5]/8"
                          >
                            <Route className="h-3.5 w-3.5" /> View Trip
                          </button>
                          <button
                            data-testid={`etransfer-pay-${e.id}`}
                            disabled={paid || loadingId === e.id}
                            onClick={() => (e.has_dispute ? setConfirm(e) : pay(e))}
                            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-40 ${e.has_dispute ? "bg-[#d97706] hover:bg-[#b45309]" : "bg-[#7c3aed] hover:bg-[#6d28d9]"}`}
                          >
                            {loadingId === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : e.has_dispute ? <ShieldAlert className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />} Pay
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="etransfer-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#e4002b]">
              <ShieldAlert className="h-5 w-5" /> Open dispute — hold on
            </DialogTitle>
            <p className="text-sm text-[#565b73]">
              {confirm?.requester} has {confirm?.disputes?.length || 0} open dispute{(confirm?.disputes?.length || 0) === 1 ? "" : "s"}.
              Paying {confirm ? money(confirm.amount) : ""} instantly may be difficult to recover if the dispute is upheld.
            </p>
          </DialogHeader>
          {confirm?.disputes?.length > 0 && (
            <div className="max-h-[30vh] space-y-2 overflow-y-auto">
              {confirm.disputes.map((d) => (
                <div key={d.id} className="rounded-lg border border-[#e4002b]/15 bg-[#e4002b]/4 p-3">
                  <p className="text-sm font-semibold text-[#12142b]">{d.route}</p>
                  <p className="mt-0.5 text-xs text-[#565b73]">Rider: {d.rider} · {money(d.amount)} refund</p>
                  <p className="mt-1 text-xs text-[#12142b]"><span className="font-semibold">Reason:</span> {d.reason}</p>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button
              data-testid="etransfer-confirm-cancel"
              onClick={() => setConfirm(null)}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73]"
            >
              Cancel
            </button>
            <button
              data-testid="etransfer-confirm-pay"
              onClick={() => { const c = confirm; setConfirm(null); pay(c); }}
              className="flex items-center gap-2 rounded-xl bg-[#d97706] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#b45309]"
            >
              <ShieldAlert className="h-4 w-4" /> Pay Anyway
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DriverTripsDialog request={tripsFor} onClose={() => setTripsFor(null)} />
    </Dialog>
  );
}


const TRIP_STATUS_STYLE = {
  completed: { label: "Completed", color: "#16a34a", bg: "#16a34a14" },
  open: { label: "Open", color: "#2340b5", bg: "#2340b514" },
  cancelled: { label: "Cancelled", color: "#e4002b", bg: "#e4002b14" },
};

export function DriverTripsDialog({ request, onClose }) {
  const [data, setData] = useState(null);
  const [completedOnly, setCompletedOnly] = useState(false);

  useEffect(() => {
    if (!request?.driver_id) { setData(null); return; }
    setData(null);
    setCompletedOnly(false);
    adminApi.get(`/finance/driver-trips/${request.driver_id}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, completed_count: 0, estimated_earnings: 0 }));
  }, [request]);

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" }) : "—";
  const name = request?.requester || request?.driver || "driver";
  const visible = data ? (completedOnly ? data.items.filter((t) => t.status === "completed") : data.items) : [];

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="driver-trips-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Route className="h-5 w-5 text-[#2340b5]" /> Trips by {name}</DialogTitle>
          <p className="text-sm text-[#565b73]">The rides this driver posted — the source of the {request ? money(request.amount) : ""} payout request.</p>
        </DialogHeader>

        {data && (
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-[#f4f6fb] p-3 text-center">
              <p className="text-xs text-[#565b73]">Total trips</p>
              <p className="font-display text-xl font-black text-[#12142b]">{data.total}</p>
            </div>
            <div className="rounded-xl bg-[#16a34a]/8 p-3 text-center">
              <p className="text-xs text-[#565b73]">Completed</p>
              <p className="font-display text-xl font-black text-[#16a34a]">{data.completed_count}</p>
            </div>
            <div className="rounded-xl bg-[#2340b5]/8 p-3 text-center">
              <p className="text-xs text-[#565b73]">Est. earnings</p>
              <p className="font-display text-xl font-black text-[#2340b5]">{money(data.estimated_earnings)}</p>
            </div>
          </div>
        )}

        {data && data.total > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#565b73]">Showing {visible.length} of {data.total} trip{data.total === 1 ? "" : "s"}</p>
            <button
              data-testid="trips-completed-toggle"
              onClick={() => setCompletedOnly((v) => !v)}
              aria-pressed={completedOnly}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${completedOnly ? "bg-[#16a34a] text-white hover:bg-[#15803d]" : "border border-[#16a34a]/30 text-[#16a34a] hover:bg-[#16a34a]/8"}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> {completedOnly ? "Showing completed" : "Completed only"}
            </button>
          </div>
        )}

        <div className="mt-1 max-h-[52vh] space-y-2 overflow-y-auto">
          {!data && <p className="py-10 text-center text-sm text-[#565b73]">Loading trips…</p>}
          {data && data.items.length === 0 && <p className="py-10 text-center text-sm text-[#565b73]">No trips found for this driver.</p>}
          {data && data.items.length > 0 && visible.length === 0 && <p className="py-10 text-center text-sm text-[#565b73]">No completed trips for this driver.</p>}
          {visible.map((t) => {
            const st = TRIP_STATUS_STYLE[t.status] || { label: t.status, color: "#565b73", bg: "#565b7314" };
            return (
              <div key={t.id} data-testid={`driver-trip-${t.id}`} className="rounded-xl border border-[#12142b]/8 bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-[#12142b]">
                    <MapPin className="h-4 w-4 shrink-0 text-[#2340b5]" /> {t.origin} → {t.destination}
                  </p>
                  <span className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: st.color, backgroundColor: st.bg }}>
                    {st.label}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#565b73]">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(t.depart_at)}</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {t.seats_booked}/{t.seats} seats</span>
                  <span className="flex items-center gap-1 font-semibold text-[#12142b]"><CreditCard className="h-3 w-3 text-[#565b73]" /> {money(t.price)}/seat</span>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}


const PT_STATUS_STYLE = {
  upcoming: { label: "Upcoming", color: "#2340b5", bg: "#2340b514" },
  "in progress": { label: "In Progress", color: "#d97706", bg: "#f59e0b14" },
};

function TripAvatar({ name, tint = "#2340b5" }) {
  return (
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-bold" style={{ backgroundColor: `${tint}1a`, color: tint }}>
      {(name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
    </span>
  );
}

export function PendingTripsAllDialog({ open, onClose }) {
  const [items, setItems] = useState(null);
  const [messagesFor, setMessagesFor] = useState(null);
  const [contact, setContact] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(() => {
    adminApi.get("/finance/pending-trips", { params: { page: 1, page_size: 100 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);
  useEffect(() => { if (open) { setItems(null); setExpanded(new Set()); load(); } }, [open, load]);

  const groups = useMemo(() => {
    if (!items) return null;
    const map = new Map();
    for (const t of items) {
      const key = t.ride_id || t.id;
      if (!map.has(key)) {
        map.set(key, { key, ride_id: t.ride_id, driver: t.driver, driver_id: t.driver_id,
          route: t.route, trip_date: t.trip_date, status: t.status, amount: 0, riders: [] });
      }
      const g = map.get(key);
      g.amount = Math.round((g.amount + (t.amount || 0)) * 100) / 100;
      g.riders.push(t);
      if (t.trip_date && (!g.trip_date || t.trip_date < g.trip_date)) g.trip_date = t.trip_date;
    }
    return Array.from(map.values());
  }, [items]);

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="pending-trips-all-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-[#2340b5]" /> Pending Trips — Funds in Escrow</DialogTitle>
          <p className="text-sm text-[#565b73]">Trips holding funds in escrow. Expand a trip to see every rider; use per-trip and per-rider actions.</p>
        </DialogHeader>

        <div className="max-h-[62vh] overflow-y-auto">
          {!groups && <p className="py-10 text-center text-sm text-[#565b73]">Loading pending trips…</p>}
          {groups && groups.length === 0 && <p className="py-10 text-center text-sm text-[#565b73]">No pending trips.</p>}
          {groups && groups.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]">
                  <th className="py-2 pr-2">Driver</th>
                  <th className="py-2 pr-2">Route</th>
                  <th className="py-2 pr-2">Riders</th>
                  <th className="py-2 pr-2">Trip Date</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Escrow</th>
                  <th className="py-2 pr-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const st = PT_STATUS_STYLE[(g.status || "").toLowerCase()] || PT_STATUS_STYLE.upcoming;
                  const isOpen = expanded.has(g.key);
                  return (
                    <Fragment key={g.key}>
                    <tr
                      data-testid={`pending-trip-row-${g.key}`}
                      onClick={() => toggle(g.key)}
                      className="cursor-pointer border-b border-[#12142b]/5 hover:bg-[#f9fafd]"
                    >
                      <td className="py-3 pr-2">
                        <div className="flex items-center gap-2">
                          <span data-testid={`pending-trip-expand-${g.key}`} className="text-[#565b73]">
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </span>
                          <TripAvatar name={g.driver} />
                          <span className="font-medium text-[#12142b]">{g.driver}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-2 text-[#565b73]">{g.route}</td>
                      <td className="py-3 pr-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2340b5]/8 px-2.5 py-1 text-xs font-semibold text-[#2340b5]">
                          <Users2 className="h-3 w-3" /> {g.riders.length} rider{g.riders.length === 1 ? "" : "s"}
                        </span>
                      </td>
                      <td className="py-3 pr-2 text-[#565b73]">{fmtDate(g.trip_date)}</td>
                      <td className="py-3 pr-2">
                        <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: st.color, backgroundColor: st.bg }}>{st.label}</span>
                      </td>
                      <td className="py-3 pr-2 font-semibold text-[#12142b]">{money(g.amount)}</td>
                      <td className="py-3 pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`pending-trip-actions-${g.key}`} className="ml-auto grid h-8 w-8 place-items-center rounded-lg text-[#565b73] transition-colors hover:bg-[#eef1f8]">
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" sideOffset={4} collisionPadding={8} className="w-52 border border-[#12142b]/10 bg-white shadow-xl">
                            <DropdownMenuItem data-testid={`pending-expand-riders-${g.key}`} onClick={() => setExpanded((p) => new Set(p).add(g.key))} className="gap-2.5 text-[#3a3f57]">
                              <Users2 className="h-4 w-4" strokeWidth={1.75} /> View Riders
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid={`pending-contact-driver-${g.key}`} onClick={() => setContact({ id: g.driver_id, name: g.driver })} className="gap-2.5 text-[#3a3f57]">
                              <UserCog className="h-4 w-4" strokeWidth={1.75} /> Contact Driver
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                    {isOpen && g.riders.map((r) => (
                      <tr key={r.id} data-testid={`pending-rider-row-${r.rider_id || r.id}`} className="border-b border-[#12142b]/5 bg-[#f9fafd]">
                        <td className="py-2.5 pr-2" colSpan={2}>
                          <div className="flex items-center gap-2 pl-10">
                            <TripAvatar name={r.rider} tint="#16a34a" />
                            <div>
                              <p className="text-sm font-semibold text-[#12142b]">{r.rider}</p>
                              <p className="flex items-center gap-1.5 text-xs text-[#565b73]"><Phone className="h-3 w-3" /> {r.rider_phone} · <Mail className="h-3 w-3" /> {r.rider_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 pr-2 text-xs text-[#565b73]">1 seat</td>
                        <td className="py-2.5 pr-2" />
                        <td className="py-2.5 pr-2" />
                        <td className="py-2.5 pr-2 font-semibold text-[#12142b]">{money(r.amount)}</td>
                        <td className="py-2.5 pr-2">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              data-testid={`pending-rider-messages-${r.rider_id || r.id}`}
                              onClick={() => setMessagesFor(r)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-[#2340b5]/25 px-2.5 py-1.5 text-xs font-semibold text-[#2340b5] transition-colors hover:bg-[#2340b5]/8"
                            >
                              <MessageSquare className="h-3.5 w-3.5" /> Messages
                            </button>
                            <button
                              data-testid={`pending-rider-contact-${r.rider_id || r.id}`}
                              onClick={() => setContact({ id: r.rider_id, name: r.rider })}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-[#16a34a]/30 px-2.5 py-1.5 text-xs font-semibold text-[#16a34a] transition-colors hover:bg-[#16a34a]/8"
                            >
                              <Send className="h-3.5 w-3.5" /> Contact
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>

      <TripMessagesDialog trip={messagesFor} onClose={() => setMessagesFor(null)} />
      <SendMessageDialog user={contact} onClose={() => setContact(null)} />
    </Dialog>
  );
}

export function SettleRideDialog({ trip, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const open = !!trip;
  const big = (trip?.amount || 0) >= 100;
  const stripe = !!trip?.stripe_connected;
  const settle = async () => {
    if (!trip?.ride_id) { toast.error("No ride linked to this trip"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/rides/${trip.ride_id}/settle`, { method: stripe ? "stripe" : "manual" });
      toast.success(data.message || "Ride settled — driver payout released");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Settle failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="settle-ride-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-[#16a34a]" /> Settle & Pay Driver</DialogTitle>
        </DialogHeader>
        {trip && (
          <div className="space-y-3">
            <div className="rounded-xl border border-[#12142b]/8 bg-[#f9fafd] p-4 text-sm">
              <div className="flex justify-between py-1"><span className="text-[#565b73]">Driver</span><span className="font-semibold text-[#12142b]">{trip.driver}</span></div>
              <div className="flex justify-between py-1"><span className="text-[#565b73]">Route</span><span className="text-[#12142b]">{trip.route || "—"}</span></div>
              <div className="flex justify-between py-1"><span className="text-[#565b73]">Payout amount</span><span className="font-bold text-[#12142b]">${(trip.amount ?? 0).toFixed(2)}</span></div>
              <div className="flex justify-between py-1"><span className="text-[#565b73]">Payout method</span>
                {stripe
                  ? <span data-testid="settle-method-stripe" className="rounded-full bg-[#635bff]/12 px-2 py-0.5 text-xs font-bold text-[#635bff]">Stripe</span>
                  : <span data-testid="settle-method-manual" className="rounded-full bg-[#f59e0b]/14 px-2 py-0.5 text-xs font-bold text-[#b45309]">Manual E-Transfer</span>}
              </div>
            </div>
            {stripe ? (
              <p className="text-xs text-[#565b73]">This driver completed Stripe onboarding. Confirming credits their in-app wallet for this ride — they withdraw instantly to their connected Stripe account in the app. It settles the whole ride (all its completed bookings).</p>
            ) : (
              <p className="text-xs text-[#565b73]">This driver has no connected Stripe account, so payout stays manual: this releases escrow and you send the e-transfer yourself. It settles the whole ride (all its completed bookings).</p>
            )}
            {big && <p className="flex items-center gap-1.5 rounded-lg bg-[#e4002b]/8 px-3 py-2 text-xs font-semibold text-[#e4002b]"><AlertTriangle className="h-4 w-4" /> Large payout — double-check before settling.</p>}
          </div>
        )}
        <DialogFooter>
          <button data-testid="settle-cancel" onClick={onClose} className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73] hover:bg-[#f4f6fb]">Cancel</button>
          <button data-testid="settle-confirm" onClick={settle} disabled={busy} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60 ${stripe ? "bg-[#635bff] hover:bg-[#524bd6]" : "bg-[#16a34a] hover:bg-[#15803d]"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {stripe ? `Pay via Stripe $${(trip?.amount ?? 0).toFixed(2)}` : `Settle & Pay $${(trip?.amount ?? 0).toFixed(2)}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AwaitingApprovalDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [contact, setContact] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/finance/awaiting-approval")
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }));
  }, []);
  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const items = data?.items || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="awaiting-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-[#f59e0b]" /> Awaiting Driver Approval</DialogTitle>
          <p className="text-sm text-[#565b73]">Bookings placed by riders that the driver hasn&apos;t approved yet — no funds are escrowed until approval. Once approved, they move to Pending Trips.</p>
        </DialogHeader>

        <div className="max-h-[62vh] overflow-y-auto">
          {!data && <p className="py-10 text-center text-sm text-[#565b73]">Loading…</p>}
          {data && items.length === 0 && <p className="py-10 text-center text-sm text-[#565b73]">No bookings awaiting approval.</p>}
          {items.length > 0 && (
            <table className="w-full text-left text-sm" data-testid="awaiting-approval-table">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]">
                  <th className="py-2 pr-2">Rider</th>
                  <th className="py-2 pr-2">Driver</th>
                  <th className="py-2 pr-2">Route</th>
                  <th className="py-2 pr-2">Booked</th>
                  <th className="py-2 pr-2">Seats</th>
                  <th className="py-2 pr-2">Amount</th>
                  <th className="py-2 pr-2 text-right">Contact</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} data-testid={`awaiting-row-${t.id}`} className="border-b border-[#12142b]/5 last:border-0">
                    <td className="py-2.5 pr-2 font-medium text-[#12142b]">{t.rider}</td>
                    <td className="py-2.5 pr-2 text-[#565b73]">{t.driver}</td>
                    <td className="py-2.5 pr-2 text-[#565b73]">{t.route}</td>
                    <td className="py-2.5 pr-2 text-[#565b73]">{fmtDate(t.booked_at)}</td>
                    <td className="py-2.5 pr-2 text-[#565b73]">{t.seats}</td>
                    <td className="py-2.5 pr-2 font-semibold text-[#12142b]">${(t.amount ?? 0).toFixed(2)}</td>
                    <td className="py-2.5 pr-2 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`awaiting-contact-rider-${t.id}`}
                          onClick={() => setContact({ id: t.rider_id, name: t.rider, phone: t.rider_phone, email: t.rider_email })}
                          className="rounded-lg border border-[#12142b]/12 px-2.5 py-1 text-xs font-semibold text-[#2340b5] hover:bg-[#f9fafd]"
                        >Rider</button>
                        <button
                          data-testid={`awaiting-contact-driver-${t.id}`}
                          onClick={() => setContact({ id: t.driver_id, name: t.driver, phone: t.driver_phone, email: t.driver_email })}
                          className="rounded-lg border border-[#12142b]/12 px-2.5 py-1 text-xs font-semibold text-[#2340b5] hover:bg-[#f9fafd]"
                        >Driver</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>

      <SendMessageDialog user={contact} onClose={() => setContact(null)} />
    </Dialog>
  );
}


export function TripMessagesDialog({ trip, onClose }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [recipient, setRecipient] = useState("rider");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!trip?.id) { setData(null); return; }
    setData(null);
    setReply("");
    setRecipient("rider");
    adminApi.get(`/finance/trip-messages/${trip.id}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ messages: [], rider: {}, driver: {} }));
  }, [trip]);

  const fmtTime = (s) => s ? new Date(s).toLocaleString("en-CA", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
  const driverName = data?.driver?.name || trip?.driver;
  const riderName = data?.rider?.name || trip?.rider;
  const targetId = recipient === "driver" ? data?.driver?.id : data?.rider?.id;
  const targetName = recipient === "driver" ? driverName : riderName;

  const send = async () => {
    const text = reply.trim();
    if (!text || !targetId) return;
    setSending(true);
    try {
      await adminApi.post(`/users/${targetId}/message`, { text });
      setData((d) => ({
        ...d,
        messages: [...(d?.messages || []), {
          id: `admin-${Date.now()}`, sender: `admin→${recipient}`, text,
          created_at: new Date().toISOString(),
        }],
      }));
      setReply("");
      toast.success(`Reply sent to ${targetName}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="trip-messages-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#2340b5]" /> Trip Messages</DialogTitle>
          <p className="text-sm text-[#565b73]">{trip?.route} · {trip?.rider} ↔ {trip?.driver}</p>
        </DialogHeader>
        <div className="max-h-[48vh] space-y-3 overflow-y-auto rounded-xl bg-[#f4f6fb] p-4">
          {!data && <p className="py-8 text-center text-sm text-[#565b73]">Loading messages…</p>}
          {data && data.messages.length === 0 && <p className="py-8 text-center text-sm text-[#565b73]">No messages exchanged for this trip yet.</p>}
          {data && data.messages.map((m) => {
            const sender = (m.sender || "").toLowerCase();
            const isAdmin = sender.includes("admin") || sender.includes("support");
            const isDriver = !isAdmin && sender.includes("driver");
            if (isAdmin) {
              const toName = sender.includes("driver") ? driverName : riderName;
              return (
                <div key={m.id} className="flex flex-col items-end">
                  <div className="max-w-[80%] rounded-2xl bg-[#16a34a] px-3.5 py-2 text-sm text-white" data-testid="trip-admin-reply">{m.text}</div>
                  <span className="mt-1 text-[10px] text-[#565b73]">SafeRide Support → {toName} · {fmtTime(m.created_at)}</span>
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex flex-col ${isDriver ? "items-end" : "items-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${isDriver ? "bg-[#2340b5] text-white" : "bg-white text-[#12142b] border border-[#12142b]/8"}`}>
                  {m.text}
                </div>
                <span className="mt-1 text-[10px] text-[#565b73]">{isDriver ? driverName : riderName} · {fmtTime(m.created_at)}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[#565b73]">Reply to:</span>
            <div className="inline-flex overflow-hidden rounded-lg border border-[#12142b]/12">
              <button
                data-testid="trip-reply-to-rider"
                onClick={() => setRecipient("rider")}
                className={`px-3 py-1.5 font-semibold transition-colors ${recipient === "rider" ? "bg-[#2340b5] text-white" : "bg-white text-[#565b73] hover:bg-[#f4f6fb]"}`}
              >
                Rider ({riderName})
              </button>
              <button
                data-testid="trip-reply-to-driver"
                onClick={() => setRecipient("driver")}
                className={`px-3 py-1.5 font-semibold transition-colors ${recipient === "driver" ? "bg-[#2340b5] text-white" : "bg-white text-[#565b73] hover:bg-[#f4f6fb]"}`}
              >
                Driver ({driverName})
              </button>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <textarea
              data-testid="trip-reply-input"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
              rows={2}
              maxLength={1000}
              placeholder={`Reply to ${targetName} as SafeRide Support…`}
              className="flex-1 resize-none rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
            />
            <button
              data-testid="trip-reply-send"
              disabled={sending || !reply.trim() || !targetId}
              onClick={send}
              className="flex h-11 items-center gap-2 rounded-xl bg-[#2340b5] px-4 text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}



const TX_STATUS_STYLE = {
  completed: { color: "#16a34a", bg: "#16a34a14" },
  refunded: { color: "#e4002b", bg: "#e4002b14" },
  cancelled: { color: "#565b73", bg: "#565b7314" },
  pending: { color: "#b45309", bg: "#f59e0b14" },
};

export function ProfitBreakdownDialog({ open, period, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    adminApi.get("/finance/profit-breakdown", { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => setData({ summary: {}, flow: {}, sources: [], transactions: [] }));
  }, [open, period]);

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-CA", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const s = data?.summary || {};
  const fees = [
    ["Booking fees", s.booking_fees],
    ["E-transfer fees", s.etransfer_fees],
    ["Ad fees", s.ad_fees],
    ["Other fees (pins)", s.pin_fees],
  ];
  const feeTotal = fees.reduce((sum, [, v]) => sum + (v || 0), 0);
  const maxGross = Math.max(1, ...(data?.sources || []).map((x) => Math.abs(x.gross)));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="profit-breakdown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[#16a34a]" /> Platform Profit — Where it comes from</DialogTitle>
          <p className="text-sm text-[#565b73]">Fee income, money flow by transaction type and the underlying transactions for this period.</p>
        </DialogHeader>

        {!data && <p className="py-10 text-center text-sm text-[#565b73]">Loading breakdown…</p>}

        {data && (
          <div className="max-h-[64vh] space-y-5 overflow-y-auto pr-1">
            {/* Summary tiles */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-[#f4f6fb] p-3 text-center">
                <p className="text-xs text-[#565b73]">Total Income</p>
                <p className="font-display text-xl font-black text-[#12142b]">{money(s.total_income)}</p>
              </div>
              <div className="rounded-xl bg-[#e4002b]/8 p-3 text-center">
                <p className="text-xs text-[#565b73]">Refunds & Chargebacks</p>
                <p className="font-display text-xl font-black text-[#e4002b]">{money(s.refunds_chargebacks)}</p>
              </div>
              <div className="rounded-xl bg-[#16a34a]/8 p-3 text-center">
                <p className="text-xs text-[#565b73]">Platform Profit</p>
                <p className="font-display text-xl font-black text-[#16a34a]">{money(s.profit)}</p>
              </div>
            </div>

            {/* Fee sources with contribution bars */}
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#12142b]"><Receipt className="h-4 w-4 text-[#565b73]" /> Fee income sources</p>
              <div className="space-y-2" data-testid="profit-fee-sources">
                {fees.map(([label, val]) => {
                  const pct = feeTotal > 0 ? Math.round(((val || 0) / feeTotal) * 100) : 0;
                  return (
                    <div key={label}>
                      <div className="flex justify-between text-sm"><span className="text-[#565b73]">{label}</span><span className="font-semibold text-[#12142b]">{money(val)}{feeTotal > 0 ? ` · ${pct}%` : ""}</span></div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#f4f6fb]"><div className="h-full rounded-full bg-[#2340b5]" style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
                {feeTotal === 0 && <p className="text-xs text-[#565b73]">No realized fee income recorded for this period yet.</p>}
              </div>
            </div>

            {/* Money flow by transaction type */}
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#12142b]"><ArrowRight className="h-4 w-4 text-[#565b73]" /> Money flow by type</p>
              <table className="w-full text-left text-sm" data-testid="profit-sources-table">
                <thead>
                  <tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]">
                    <th className="py-2 pr-2">Type</th>
                    <th className="py-2 pr-2">Count</th>
                    <th className="py-2 pr-2">Gross</th>
                    <th className="py-2 pr-2">Driver Earn</th>
                    <th className="py-2 pr-2">Refunds</th>
                    <th className="py-2 pr-2 text-right">Net to Platform</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sources.map((x) => (
                    <tr key={x.type} data-testid={`profit-source-${x.type.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border-b border-[#12142b]/5 last:border-0">
                      <td className="py-2.5 pr-2">
                        <span className="font-medium text-[#12142b]">{x.type}</span>
                        <div className="mt-1 h-1 w-24 overflow-hidden rounded-full bg-[#f4f6fb]"><div className="h-full rounded-full bg-[#16a34a]" style={{ width: `${Math.round((Math.abs(x.gross) / maxGross) * 100)}%` }} /></div>
                      </td>
                      <td className="py-2.5 pr-2 text-[#565b73]">{x.count}</td>
                      <td className="py-2.5 pr-2 font-semibold text-[#12142b]">{money(x.gross)}</td>
                      <td className="py-2.5 pr-2 text-[#565b73]">{money(x.driver_earn)}</td>
                      <td className="py-2.5 pr-2 text-[#e4002b]">{money(x.refunds)}</td>
                      <td className="py-2.5 pr-2 text-right font-semibold" style={{ color: x.net >= 0 ? "#16a34a" : "#e4002b" }}>{money(x.net)}</td>
                    </tr>
                  ))}
                  {data.sources.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#565b73]">No transactions in this period.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Contributing transactions */}
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#12142b]"><CreditCard className="h-4 w-4 text-[#565b73]" /> Contributing transactions ({data.flow?.transaction_count ?? data.transactions.length})</p>
              <div className="space-y-2" data-testid="profit-transactions">
                {data.transactions.map((t) => {
                  const stt = TX_STATUS_STYLE[(t.status || "").toLowerCase()] || TX_STATUS_STYLE.pending;
                  return (
                    <div key={t.id} data-testid={`profit-tx-${t.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#12142b]/8 bg-white p-3">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-sm font-semibold text-[#12142b]">
                          {t.type}
                          {t.short_id && <span className="text-[10px] font-normal text-[#a3abc7]">{t.short_id}</span>}
                        </p>
                        <p className="truncate text-xs text-[#565b73]">{t.from_name} <ArrowRight className="inline h-3 w-3" /> {t.to_name} · {fmtDate(t.created_at)}{t.card_brand ? ` · ${t.card_brand}${t.card_last4 ? ` ••${t.card_last4}` : ""}` : ""}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {t.refund > 0 && <span className="text-xs font-semibold text-[#e4002b]">-{money(t.refund)}</span>}
                        <span className="font-semibold text-[#12142b]">{money(t.amount)}</span>
                        <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: stt.color, backgroundColor: stt.bg }}>{t.status}</span>
                      </div>
                    </div>
                  );
                })}
                {data.transactions.length === 0 && <p className="py-6 text-center text-sm text-[#565b73]">No transactions found.</p>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


const SEVERITY_STYLE = {
  high: "bg-[#e4002b]/10 text-[#e4002b]",
  medium: "bg-[#f59e0b]/12 text-[#b45309]",
  low: "bg-[#94a3b8]/15 text-[#475569]",
};

export function DisputeDialog({ trip, onClose, onResolved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState("");
  const [confirm, setConfirm] = useState(""); // "refund" | "release"

  const load = useCallback(() => {
    if (!trip?.booking_id) return;
    adminApi.get(`/finance/bookings/${trip.booking_id}/disputes`)
      .then((r) => setData(r.data))
      .catch(() => setData({ disputes: [], has_dispute: false }));
  }, [trip?.booking_id]);
  useEffect(() => { setData(null); setConfirm(""); load(); }, [load]);

  if (!trip) return null;

  const act = async (action) => {
    setLoading(action);
    try {
      const { data: res } = await adminApi.post(`/finance/bookings/${trip.booking_id}/${action === "refund" ? "refund" : "release-funds"}`, {});
      toast.success(res.message || "Dispute resolved");
      onResolved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setConfirm("");
    } finally {
      setLoading("");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-[#e4002b]" /> Dispute details</DialogTitle>
        </DialogHeader>

        {/* trip summary */}
        <div className="rounded-xl bg-[#f4f6fb] p-4 text-sm" data-testid="dispute-trip-summary">
          <div className="flex justify-between py-1"><span className="text-[#565b73]">Driver</span><span className="font-semibold text-[#12142b]">{trip.driver}</span></div>
          {trip.rider && <div className="flex justify-between py-1"><span className="text-[#565b73]">Rider</span><span className="font-semibold text-[#12142b]">{trip.rider}</span></div>}
          {trip.route && <div className="flex justify-between py-1"><span className="text-[#565b73]">Route</span><span className="font-semibold text-[#12142b]">{trip.route}</span></div>}
          <div className="flex justify-between py-1"><span className="text-[#565b73]">Amount held</span><span className="font-semibold text-[#12142b]">{money(trip.amount)}</span></div>
          <div className="flex justify-between py-1"><span className="text-[#565b73]">Booking</span><span className="font-mono text-xs text-[#565b73]">{trip.booking_id}</span></div>
        </div>

        {/* disputes */}
        <div className="mt-4 space-y-2" data-testid="dispute-list">
          {data === null && <p className="py-4 text-center text-sm text-[#565b73]">Loading disputes…</p>}
          {data && data.disputes.length === 0 && <p className="py-4 text-center text-sm text-[#565b73]">No dispute records found for this booking.</p>}
          {(data?.disputes || []).map((d, i) => (
            <div key={d.ref_id || i} className="rounded-xl border border-[#12142b]/8 p-3.5" data-testid={`dispute-item-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-[#12142b]">{d.label || d.kind}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SEVERITY_STYLE[d.severity] || SEVERITY_STYLE.low}`}>{d.severity || "—"}</span>
              </div>
              {d.details && <p className="mt-1 text-xs text-[#565b73]">{d.details}</p>}
              <p className="mt-1 text-[11px] text-[#565b73]/70">{d.at ? new Date(d.at).toLocaleString("en-CA", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}{d.ref_id ? ` · ref ${d.ref_id.slice(0, 8)}` : ""}</p>
            </div>
          ))}
        </div>

        {/* resolution actions */}
        {confirm ? (
          <div className="mt-4 rounded-xl border border-[#e4002b]/25 bg-[#e4002b]/5 p-4" data-testid="dispute-confirm">
            <p className="text-sm font-semibold text-[#12142b]">
              {confirm === "refund" ? `Refund ${money(trip.amount)} to the rider?` : `Release ${money(trip.amount)} to ${trip.driver}?`}
            </p>
            <p className="mt-1 text-xs text-[#565b73]">This resolves the dispute and cannot be undone.</p>
            <div className="mt-3 flex gap-2">
              <button data-testid="dispute-confirm-yes" onClick={() => act(confirm)} disabled={!!loading}
                className="flex items-center gap-2 rounded-lg bg-[#2340b5] px-4 py-2 text-xs font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirm
              </button>
              <button data-testid="dispute-confirm-no" onClick={() => setConfirm("")} disabled={!!loading}
                className="rounded-lg border border-[#12142b]/12 px-4 py-2 text-xs font-semibold text-[#12142b]">Cancel</button>
            </div>
          </div>
        ) : (
          <DialogFooter className="mt-4 gap-2 sm:justify-between">
            <button data-testid="dispute-refund-btn" onClick={() => setConfirm("refund")}
              className="rounded-xl border border-[#e4002b]/40 px-4 py-2.5 text-sm font-bold text-[#e4002b] hover:bg-[#e4002b]/6">
              Refund rider
            </button>
            <button data-testid="dispute-release-btn" onClick={() => setConfirm("release")}
              className="rounded-xl bg-[#16a34a] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#15803d]">
              Release funds to driver
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
